import { SxProps, Theme } from "@mui/material";
import Box, { BoxProps } from "@mui/material/Box";
import Grid from "@mui/material/GridLegacy";
import { useLocalStorageValue } from "@react-hookz/web";
import {
  MutableRefObject,
  ReactElement,
  useEffect,
  ComponentType,
  ReactNode,
  useRef,
  useState,
} from "react";
import ReactPlayer from "react-player";
import { BaseReactPlayerProps } from "react-player/types/base";
import { CircularProgress } from "../../Feedback/CircularProgress/CircularProgress";
import { MediaControl } from "../MediaControl/MediaControl";
import useFullscreenControlState from "../MediaControl/useFullscreenControlState";
import { MarkType } from "../Seeker/type";
import { useKeyboardControl } from "./useKeyboardControl";

const VideoWrapper: ComponentType<{ children: ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

export const REACT_PLAYER_CONFIG = {
  file: {
    attributes: {
      controlsList: "nodownload",
      role: "media",
      disableRemotePlayback: true,
    },
  },
};

export type VideoPlayerProps = {
  Overlay?: ReactElement | null;
  OverlayToggle?: ReactElement;
  containerRef?: MutableRefObject<HTMLDivElement | null>;
  disableControl?: boolean;
  disableSeek?: boolean;
  hideControls?: boolean;
  fullScreenMode?: boolean;
  compactMode?: boolean;
  framesPerSecond: number;
  marks?: MarkType[];
  onClickFullscreen?: () => void;
  onClickMark?: (mark: MarkType) => void;
  onDuration?: (duration: number) => void;
  onPause?: () => void;
  onPlay?: () => void;
  onProgress?: (progress: number) => void;
  onLoad?: () => void;
  onError?: (error: ErrorEvent) => void;
  onToggleFullscreenControl?: (toggle: boolean) => void;
  playing?: boolean;
  muted?: boolean;
  progress?: number;
  seek?: number;
  src: string;
  videoRef?: MutableRefObject<HTMLVideoElement | null>;
  showRewindControls?: boolean;
};

type VideoStateType = {
  duration: number;
  muted: boolean;
  playbackRate: number;
  playing: boolean;
  progress: number;
};

export const useVideoPlayer = ({
  containerRef,
  disableControl,
  disableSeek,
  framesPerSecond,
  fullScreenMode,
  compactMode,
  marks,
  onClickFullscreen,
  onClickMark,
  onToggleFullscreenControl,
  onLoad,
  onError,
  OverlayToggle,
  Overlay = null,
  src,
  videoRef = { current: null },
  showRewindControls,
  ...controlledState
}: VideoPlayerProps): [
  ReactElement,
  ReactElement,
  VideoStateType & { showFullscreenControl: boolean },
] => {
  const playerRef = useRef<ReactPlayer>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [videoState, setVideoState] = useState<VideoStateType>({
    duration: 0,
    muted: false,
    playbackRate: 1,
    playing: false,
    progress: 0,
  });

  const {
    showControl: showFullscreenControl,
    toggleControl: toggleFullscreenControl,
    autoHideControl: autoHideFullscreenControl,
  } = useFullscreenControlState({ onChange: onToggleFullscreenControl });

  const { value: playerMuted } = useLocalStorageValue("player-muted", {
    defaultValue: true,
    initializeWithValue: true,
  });
  const { value: playerVolume } = useLocalStorageValue("player-volume", {
    defaultValue: 1,
    initializeWithValue: true,
  });
  const { value: playerLoop } = useLocalStorageValue("player-loop", {
    defaultValue: true,
    initializeWithValue: true,
  });
  const { value: playbackRate } = useLocalStorageValue("playback-rate", {
    defaultValue: 1,
    initializeWithValue: true,
  });

  const effectivePlaying =
    controlledState.playing !== undefined ? controlledState.playing : videoState.playing;
  const effectiveProgress =
    controlledState.progress !== undefined ? controlledState.progress : videoState.progress;

  const handlePlay = () => {
    setVideoState((prevState) => ({ ...prevState, playing: true }));
    controlledState.onPlay?.();

    if (fullScreenMode) {
      autoHideFullscreenControl(1000);
    }
  };
  const handlePause = () => {
    setVideoState((prevState) => ({ ...prevState, playing: false }));
    controlledState.onPause?.();
  };

  const handleTogglePlay = (playing: boolean) => {
    if (playing) {
      handlePlay();
    } else {
      handlePause();
    }
  };

  const handleSeek = (value: number) => {
    playerRef.current?.seekTo(value, "seconds");
  };

  const handleClick = () => {
    if (disableSeek) return;

    if (fullScreenMode) {
      toggleFullscreenControl();
    } else if (effectivePlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const handleFullscreenControlInteraction = () => {
    autoHideFullscreenControl();
  };

  const onDuration: BaseReactPlayerProps["onDuration"] = (duration: VideoStateType["duration"]) => {
    setVideoState((prevState) => ({ ...prevState, duration }));
    controlledState.onDuration?.(duration);
  };
  const onPause = () => {
    setVideoState((prevState) => ({ ...prevState, playing: false }));
    controlledState.onPause?.();
  };
  const onPlay = () => {
    setVideoState((prevState) => ({ ...prevState, playing: true }));
    controlledState.onPlay?.();
  };
  const onProgress: BaseReactPlayerProps["onProgress"] = (progress) => {
    if (progress?.played !== effectiveProgress) {
      setVideoState((prevState) => ({
        ...prevState,
        progress: progress?.playedSeconds,
      }));
      controlledState?.onProgress?.(progress?.playedSeconds);
    }
  };
  const onReady = (player: ReactPlayer) => {
    onLoad?.();
    setLoading(false);
    if (videoRef) {
      Object.assign(videoRef, {
        current: player.getInternalPlayer() as HTMLVideoElement,
      });
    }
  };

  const onBuffer = () => setLoading(true);
  const onBufferEnd = () => setLoading(false);

  useKeyboardControl({
    disabled: disableSeek,
    playing: effectivePlaying,
    framesPerSecond,
    onSeek: handleSeek,
    onToggle: (playing) => (playing ? handlePlay() : handlePause()),
    progress: effectiveProgress,
    start: 0,
    end: videoState.duration,
  });

  useEffect(() => {
    // ReactPlayer doesn't allow parent component to control the progress using prop
    // This effect will be called when `controlledState.progress` changed, (e.g. when user select revision from sidebar)
    if (controlledState.seek !== undefined) {
      handleSeek(controlledState.seek);
    }
  }, [controlledState.seek]);

  useEffect(() => {
    setLoading(true);
  }, [src]);

  const Player = (
    <Box
      className="MuiVideoPlayer-root"
      ref={containerRef}
      onClick={handleClick}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
      }}
    >
      <ReactPlayer
        className="MuiVideoPlayer-video"
        config={REACT_PLAYER_CONFIG}
        height="100%"
        loop={playerLoop}
        muted={playerMuted}
        onBuffer={onBuffer}
        onBufferEnd={onBufferEnd}
        onDuration={onDuration}
        onPause={onPause}
        onPlay={onPlay}
        onProgress={onProgress}
        onReady={onReady}
        onError={onError}
        pip={false}
        playbackRate={playbackRate}
        playing={effectivePlaying}
        playsinline
        progressInterval={100}
        ref={playerRef}
        url={src}
        volume={playerVolume}
        width="auto"
        wrapper={VideoWrapper}
      />
      {loading ? (
        <Box sx={{ position: "absolute", textAlign: "center" }}>
          <CircularProgress value={60} size={48} />
        </Box>
      ) : null}
      {Overlay}
    </Box>
  );

  const Controls = (
    <>
      <MediaControl
        disabled={disableControl}
        duration={videoState.duration}
        framesPerSecond={framesPerSecond}
        fullscreenMode={fullScreenMode}
        compactMode={compactMode}
        marks={marks}
        onClickFullscreen={() => {
          toggleFullscreenControl(true);
          onClickFullscreen?.();
        }}
        onClickMark={onClickMark}
        onInteractFullscreen={handleFullscreenControlInteraction}
        onSeek={handleSeek}
        onTogglePlay={handleTogglePlay}
        OverlayToggle={OverlayToggle}
        playing={effectivePlaying}
        progress={effectiveProgress}
        showFullscreenControl={showFullscreenControl}
        showRewindControls={showRewindControls}
      />
      {OverlayToggle && !fullScreenMode ? (
        <Grid
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            backdropFilter: "blur(16px)",
            button: { opacity: 1 },
            display: { sm: "none" },
            position: "absolute",
            zIndex: "appBar",
            top: (theme) => theme.spacing(1),
            right: (theme) => theme.spacing(1),
          }}
        >
          {OverlayToggle}
        </Grid>
      ) : null}
    </>
  );

  return [
    Player,
    Controls,
    {
      ...videoState,
      showFullscreenControl,
      playing: effectivePlaying,
      progress: effectiveProgress,
    },
  ];
};

export type VideoPlayerComponentProps = {
  fullscreenMode?: boolean;
  fullScreenControlBottomOffset?: number;
  height?: number;
  verticalAlign?: BoxProps["alignItems"];
};

const ContainerStyles = ({
  fullscreenMode,
  fullScreenControlBottomOffset,
  height,
  verticalAlign,
}: VideoPlayerComponentProps): SxProps<Theme> => ({
  position: fullscreenMode ? "fixed" : "relative",
  height: fullscreenMode ? "100vh" : height || "100%",
  left: 0,
  bottom: 0,
  ".MuiVideoPlayer-root": {
    verticalAlign,
    position: "absolute",
    video: {
      maxWidth: { xs: "100%" },
      maxHeight: { xs: "100%" },
      position: "absolute",
    },
  },
  ".MuiMediaControl-container": {
    bottom: 0,
    position: "absolute",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    backdropFilter: "blur(16px)",
    pb: (theme) =>
      fullScreenControlBottomOffset
        ? `calc(${theme.spacing(2)} + ${fullScreenControlBottomOffset}px)`
        : undefined,
  },
});

export const VideoPlayer = ({
  fullscreenMode,
  height,
  verticalAlign,
  fullScreenControlBottomOffset = 0,
  hideControls = false,
  ...hookProps
}: VideoPlayerProps & VideoPlayerComponentProps): ReactElement => {
  const [Player, Controls] = useVideoPlayer(hookProps);

  return (
    <Grid
      container
      direction="column"
      sx={ContainerStyles({
        fullscreenMode: fullscreenMode || false,
        fullScreenControlBottomOffset: fullscreenMode ? fullScreenControlBottomOffset : 0,
        height,
        verticalAlign,
      })}
    >
      <Grid item container justifyContent="center" xs position="relative">
        {Player}
      </Grid>
      {hideControls ? null : Controls}
    </Grid>
  );
};
